// MARK://dhk8/pos/features/kds/TicketBoard
// Kitchen Display board: three columns (Nuevos / En preparación / Listos) of
// @dockethub/ui-restaurant <KdsColumn>/<TicketCard>, sorted priority-desc then firedAt-asc.
// Live via @dockethub/realtime when a provider is mounted (else polls — useActiveTickets).
// Optimistic transitions via useTicketAction. Chimes on new tickets (mute toggle).
// Tablet portrait 534×738 @ DPR 1.5, data-density="compact", touch targets ≥44pt.
// Absorbed from `apps/dhk8-kds/src/pages/KdsBoardPage.tsx`.

import { useEffect, useRef, useState } from 'react'
import { Volume2, VolumeX, RefreshCw, ChefHat, ArrowLeftRight, Flame, CheckCircle2, Undo2 } from 'lucide-react'
import type { KitchenTicketDto, KitchenTicketItemDto, KitchenTicketItemStatus } from '@dockethub/ui-restaurant'
import { useActiveTickets, useTicketAction, useItemStatusAction } from './useTickets.js'
import { useKdsRealtime } from './useKdsRealtime.js'
import { useKdsStore } from './kds-store.js'
import { t } from '../../i18n/es-MX.js'
import { listStations, type PrintStationDto } from '../../lib/clients/printing.js'
import { useAuthStore } from '../../stores/auth-store.js'

function sortTickets(a: KitchenTicketDto, b: KitchenTicketDto): number {
  if (a.priority !== b.priority) return b.priority - a.priority
  return new Date(a.firedAt).getTime() - new Date(b.firedAt).getTime()
}

// ── per-item status helpers (F: estado por item) ─────────────────────────────
type ItemTone = 'fresh' | 'warn' | 'danger'
function itemTimerTone(elapsedMs: number): ItemTone {
  const min = elapsedMs / 60_000
  if (min < 5) return 'fresh'
  if (min < 12) return 'warn'
  return 'danger'
}
function fmtItemElapsed(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000))
  return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`
}
function normItemStatus(s: string | null | undefined): KitchenTicketItemStatus {
  return s === 'PREPARING' || s === 'READY' ? s : 'PENDING'
}

/** KDS ticket card with PER-ITEM PREPARING/READY controls (the cook advances each
 *  line independently). Mirrors @dockethub/ui-restaurant <TicketCard> chrome but
 *  adds an item-level control row driven by `onItemStatus` (→ updateTicketItemStatus).
 *  Built here (apps/pos) because the shared TicketCard doesn't expose item callbacks.
 *  Ticket-level verbs (start/ready/bump/recall) stay wired to the same handlers. */
function KdsItemCard({
  ticket, onStart, onReady, onBump, onRecall, onItemStatus,
}: {
  ticket: KitchenTicketDto
  onStart?: (id: string) => Promise<void> | void
  onReady?: (id: string) => Promise<void> | void
  onBump?: (id: string) => Promise<void> | void
  onRecall?: (id: string) => Promise<void> | void
  onItemStatus: (ticketId: string, itemId: string, status: KitchenTicketItemStatus) => Promise<void> | void
}) {
  const firedAt = useRef(new Date(ticket.firedAt).getTime())
  const [elapsedMs, setElapsedMs] = useState(() => Date.now() - firedAt.current)
  const [busy, setBusy] = useState(false)
  const [busyItemId, setBusyItemId] = useState<string | null>(null)
  useEffect(() => {
    const id = setInterval(() => setElapsedMs(Date.now() - firedAt.current), 1000)
    return () => clearInterval(id)
  }, [])

  const tone = itemTimerTone(elapsedMs)
  const status = ticket.status
  const isRecalled = status === 'RECALLED'

  async function runTicket(fn: ((id: string) => Promise<void> | void) | undefined) {
    if (!fn) return
    setBusy(true)
    try { await fn(ticket.id) } finally { setBusy(false) }
  }
  async function runItem(item: KitchenTicketItemDto, next: KitchenTicketItemStatus) {
    setBusyItemId(item.id)
    try { await onItemStatus(ticket.id, item.id, next) } finally { setBusyItemId(null) }
  }

  return (
    <article className={`dhk8-kdsi-ticket dhk8-kdsi-ticket--${tone}${isRecalled ? ' dhk8-kdsi-ticket--recalled' : ''}`} data-ticket-id={ticket.id}>
      <header className="dhk8-kdsi-ticket__head">
        <span className="dhk8-kdsi-ticket__num">#{ticket.ticketNumber}</span>
        {ticket.tableCode && <span className="dhk8-kdsi-ticket__table">Mesa {ticket.tableCode}</span>}
        {ticket.stationId && <span className="dhk8-kdsi-ticket__station">{ticket.stationId}</span>}
        {(ticket.orderType === 'TAKEOUT' || ticket.orderType === 'DELIVERY') && (
          <span className={`dhk8-kdsi-ticket__channel dhk8-kdsi-ticket__channel--${ticket.orderType.toLowerCase()}`}>
            {ticket.orderType === 'TAKEOUT' ? 'Para llevar' : 'Domicilio'}
          </span>
        )}
        {ticket.priority > 0 && <span className="dhk8-kdsi-ticket__prio">P{ticket.priority}</span>}
        {isRecalled && <span className="dhk8-kdsi-ticket__recall">RELLAMADO</span>}
        <span className={`dhk8-kdsi-ticket__timer dhk8-kdsi-ticket__timer--${tone}`}>{fmtItemElapsed(elapsedMs)}</span>
      </header>

      <ul className="dhk8-kdsi-ticket__items">
        {ticket.items.map((item) => {
          const st = normItemStatus(item.status)
          const done = st === 'READY'
          const preparing = st === 'PREPARING'
          const itemBusy = busyItemId === item.id
          return (
            <li key={item.id} className={`dhk8-kdsi-ticket__item dhk8-kdsi-ticket__item--${st.toLowerCase()}`}>
              <div className="dhk8-kdsi-ticket__item-main">
                <span className="dhk8-kdsi-ticket__qty">x{item.quantity}</span>
                <div className="dhk8-kdsi-ticket__item-body">
                  <span className="dhk8-kdsi-ticket__item-name">{item.name}</span>
                  {item.modifiers.length > 0 && (
                    <ul className="dhk8-kdsi-ticket__mods">{item.modifiers.map((m, i) => <li key={i}>+ {m.name}</li>)}</ul>
                  )}
                  {item.notes !== null && item.notes !== undefined && item.notes !== '' && (
                    <div className="dhk8-kdsi-ticket__notes">"{item.notes}"</div>
                  )}
                </div>
                {done && <span className="dhk8-kdsi-ticket__item-badge dhk8-kdsi-ticket__item-badge--ready">Listo</span>}
                {preparing && <span className="dhk8-kdsi-ticket__item-badge dhk8-kdsi-ticket__item-badge--prep">En prep.</span>}
              </div>
              {/* Controles por ítem: PENDING → Preparar → Listo. READY puede regresar a prep. */}
              <div className="dhk8-kdsi-ticket__item-actions">
                <button
                  type="button"
                  className={`dhk8-kdsi-item-btn${preparing ? ' dhk8-kdsi-item-btn--active' : ''}`}
                  disabled={itemBusy}
                  aria-pressed={preparing}
                  onClick={() => void runItem(item, preparing ? 'PENDING' : 'PREPARING')}
                  title={preparing ? 'Quitar de preparación' : 'Marcar en preparación'}
                >
                  <Flame size={13} /> Preparar
                </button>
                <button
                  type="button"
                  className={`dhk8-kdsi-item-btn dhk8-kdsi-item-btn--primary${done ? ' dhk8-kdsi-item-btn--active' : ''}`}
                  disabled={itemBusy}
                  aria-pressed={done}
                  onClick={() => void runItem(item, done ? 'PREPARING' : 'READY')}
                  title={done ? 'Regresar a preparación' : 'Marcar listo'}
                >
                  <CheckCircle2 size={13} /> Listo
                </button>
              </div>
            </li>
          )
        })}
      </ul>

      <footer className="dhk8-kdsi-ticket__foot">
        {(status === 'PENDING' || status === 'RECALLED') && onStart && (
          <button type="button" className="dhk8-kdsi-ticket__btn" disabled={busy} onClick={() => void runTicket(onStart)}><Flame size={14} /> Empezar</button>
        )}
        {(status === 'PENDING' || status === 'IN_PROGRESS' || status === 'RECALLED') && onReady && (
          <button type="button" className="dhk8-kdsi-ticket__btn dhk8-kdsi-ticket__btn--primary" disabled={busy} onClick={() => void runTicket(onReady)}><CheckCircle2 size={14} /> Todo listo</button>
        )}
        {status === 'READY' && onBump && (
          <button type="button" className="dhk8-kdsi-ticket__btn dhk8-kdsi-ticket__btn--bump" disabled={busy} onClick={() => void runTicket(onBump)}><CheckCircle2 size={14} /> Entregar</button>
        )}
        {(status === 'READY' || status === 'BUMPED') && onRecall && (
          <button type="button" className="dhk8-kdsi-ticket__btn dhk8-kdsi-ticket__btn--ghost" disabled={busy} onClick={() => void runTicket(onRecall)} aria-label="Rellamar"><Undo2 size={14} /></button>
        )}
      </footer>
    </article>
  )
}

/** Column wrapper for KdsItemCard — mirrors @dockethub/ui-restaurant <KdsColumn>
 *  (header + count + scroll list) but renders the item-aware card. */
function KdsItemColumn({
  title, tickets, emptyHint, onStart, onReady, onBump, onRecall, onItemStatus,
}: {
  title: string
  tickets: KitchenTicketDto[]
  emptyHint: string
  onStart?: (id: string) => Promise<void> | void
  onReady?: (id: string) => Promise<void> | void
  onBump?: (id: string) => Promise<void> | void
  onRecall?: (id: string) => Promise<void> | void
  onItemStatus: (ticketId: string, itemId: string, status: KitchenTicketItemStatus) => Promise<void> | void
}) {
  return (
    <section className="dhk8-kdsi-col">
      <div className="dhk8-kdsi-col__head">
        <span className="dhk8-kdsi-col__title">{title}</span>
        <span className="dhk8-kdsi-col__count">{tickets.length}</span>
      </div>
      <div className="dhk8-kdsi-col__list">
        {tickets.length === 0 ? (
          <div className="dhk8-kdsi-col__empty">{emptyHint}</div>
        ) : (
          tickets.map((tk) => (
            <KdsItemCard
              key={tk.id}
              ticket={tk}
              onItemStatus={onItemStatus}
              {...(onStart ? { onStart } : {})}
              {...(onReady ? { onReady } : {})}
              {...(onBump ? { onBump } : {})}
              {...(onRecall ? { onRecall } : {})}
            />
          ))
        )}
      </div>
    </section>
  )
}

/** Two-tone chime via WebAudio — no asset, no autoplay-policy fuss after a user gesture. */
function chime(): void {
  if (typeof window === 'undefined') return
  try {
    const W = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }
    const AC = W.AudioContext ?? W.webkitAudioContext
    if (!AC) return
    const ctx = new AC()
    const now = ctx.currentTime
    for (const [freq, at] of [[880, 0], [1175, 0.13]] as const) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = freq
      osc.connect(gain)
      gain.connect(ctx.destination)
      gain.gain.setValueAtTime(0.001, now + at)
      gain.gain.exponentialRampToValueAtTime(0.18, now + at + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.001, now + at + 0.18)
      osc.start(now + at)
      osc.stop(now + at + 0.2)
    }
    setTimeout(() => { void ctx.close().catch(() => {}) }, 600)
  } catch { /* ignore */ }
}

export default function TicketBoard() {
  const stationId = useKdsStore((s) => s.stationId)
  const selected = useKdsStore((s) => s.selected)
  const selectStation = useKdsStore((s) => s.selectStation)
  const clearStation = useKdsStore((s) => s.clearStation)
  const soundEnabled = useKdsStore((s) => s.soundEnabled)
  const toggleSound = useKdsStore((s) => s.toggleSound)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)

  // Fetch print stations to populate the dropdown. Falls back silently if the
  // service is unreachable — the dropdown still has Todas / Sin estación.
  const [stations, setStations] = useState<PrintStationDto[]>([])
  useEffect(() => {
    const tenantId = activeTenantId()
    if (!tenantId) return
    const ac = new AbortController()
    listStations(tenantId, ac.signal)
      .then((items) => setStations(items))
      .catch(() => { /* offline / unauthorized → empty list */ })
    return () => ac.abort()
  }, [activeTenantId])

  // Pass stationId to the backend when a specific station is selected. For
  // 'none' we fetch everything and filter client-side (no backend param exists
  // to ask for "unrouted only").
  const { data: rawTickets = [], isLoading, isError, error, refetch, isFetching } = useActiveTickets(stationId)
  const tickets = selected === 'none'
    ? rawTickets.filter((t) => !t.stationId)
    : selected === 'all'
      ? rawTickets
      : rawTickets.filter((t) => t.stationId === selected)
  const realtimeStatus = useKdsRealtime(stationId)
  const action = useTicketAction(stationId)
  const itemAction = useItemStatusAction(stationId)

  // chime on newly-appeared tickets (skip the very first load)
  const knownIds = useRef<Set<string> | null>(null)
  useEffect(() => {
    const ids = new Set(tickets.map((t) => t.id))
    if (knownIds.current === null) { knownIds.current = ids; return }
    const hasNew = [...ids].some((id) => !knownIds.current!.has(id))
    if (hasNew && soundEnabled) chime()
    knownIds.current = ids
  }, [tickets, soundEnabled])

  const onStart = (id: string) => action.mutateAsync({ id, verb: 'start' }).then(() => undefined)
  const onReady = (id: string) => action.mutateAsync({ id, verb: 'ready' }).then(() => undefined)
  const onBump = (id: string) => action.mutateAsync({ id, verb: 'bump' }).then(() => undefined)
  const onRecall = (id: string) => action.mutateAsync({ id, verb: 'recall' }).then(() => undefined)
  const onItemStatus = (ticketId: string, itemId: string, status: KitchenTicketItemStatus) =>
    itemAction.mutateAsync({ ticketId, itemId, status }).then(() => undefined)

  const colNew = tickets.filter((t) => t.status === 'PENDING' || t.status === 'RECALLED').slice().sort(sortTickets)
  const colPrep = tickets.filter((t) => t.status === 'IN_PROGRESS').slice().sort(sortTickets)
  const colReady = tickets.filter((t) => t.status === 'READY').slice().sort(sortTickets)

  const liveLabel = realtimeStatus === 'open' ? t('kds.live') : t('kds.polling')

  return (
    <div className="dhk8-kds" data-density="compact">
      <header className="dhk8-kds__bar">
        <div className="dhk8-kds__brand">
          <span className="dhk8-kds__brand-glyph"><ChefHat size={16} /></span>
          <span className="dhk8-kds__title">{t('kds.title')}</span>
          <select
            className="dhk8-kds__station-select"
            aria-label={t('kds.station')}
            value={selected}
            onChange={(e) => selectStation(e.target.value)}
          >
            <option value="all">{t('kds.allStations')}</option>
            {stations.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
            <option value="none">Sin estación</option>
          </select>
        </div>
        <div className="dhk8-kds__bar-right">
          <span className={`dhk8-kds__live dhk8-kds__live--${realtimeStatus === 'open' ? 'on' : 'poll'}`}>
            <span className="dhk8-kds__dot" /> {liveLabel}
          </span>
          <button type="button" className="dhk8-kds__icon" onClick={() => void refetch()} disabled={isFetching} aria-label={t('kds.refresh')} title={t('kds.refresh')}>
            <RefreshCw size={16} className={isFetching ? 'dhk8-kds-spin' : ''} />
          </button>
          <button type="button" className={`dhk8-kds__icon${soundEnabled ? ' dhk8-kds__icon--active' : ''}`} onClick={toggleSound} aria-pressed={soundEnabled} aria-label={soundEnabled ? t('kds.soundOn') : t('kds.soundOff')} title={soundEnabled ? t('kds.soundOn') : t('kds.soundOff')}>
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button type="button" className="dhk8-kds__icon" onClick={clearStation} aria-label={t('kds.changeStation')} title={t('kds.changeStation')}>
            <ArrowLeftRight size={16} />
          </button>
        </div>
      </header>

      {isError ? (
        <div className="dhk8-kds__note dhk8-kds__note--err">
          {t('kds.loadError')}{error instanceof Error ? `: ${error.message}` : ''}
          <button type="button" onClick={() => void refetch()}>{t('common.retry')}</button>
        </div>
      ) : isLoading ? (
        <div className="dhk8-kds__note">{t('common.loading')}</div>
      ) : (
        <div className="dhk8-kds__board">
          <KdsItemColumn title={t('kds.new')} tickets={colNew} emptyHint={t('kds.empty')} onStart={onStart} onReady={onReady} onItemStatus={onItemStatus} />
          <KdsItemColumn title={t('kds.inProgress')} tickets={colPrep} emptyHint={t('kds.empty')} onReady={onReady} onItemStatus={onItemStatus} />
          <KdsItemColumn title={t('kds.ready')} tickets={colReady} emptyHint={t('kds.empty')} onBump={onBump} onRecall={onRecall} onItemStatus={onItemStatus} />
        </div>
      )}
      <style>{styles}</style>
    </div>
  )
}

// HIG-green chrome for the KDS page (board layout, column bar, badges, controls).
// No one-sided accents; rounded fills, soft hairlines, subtle elevation. All vars
// are theme-aware `--pos-*` tokens (dark default + light via A2's [data-theme]).
const styles = `
  .dhk8-kds-spin { animation: dhk8-kds-spin 1s linear infinite; } @keyframes dhk8-kds-spin { to { transform: rotate(360deg); } }
  .dhk8-kds { display:flex; flex-direction:column; gap:var(--pos-space-3); min-height:100%; }

  .dhk8-kds__bar {
    display:flex; align-items:center; justify-content:space-between; gap:var(--pos-space-3); flex-wrap:wrap;
    padding:var(--pos-space-2) var(--pos-space-3);
    background:var(--pos-surface); border-radius:var(--pos-radius-lg);
    box-shadow:var(--pos-shadow-1); border:1px solid var(--pos-separator);
  }
  .dhk8-kds__brand { display:flex; align-items:center; gap:var(--pos-space-2); font-family:var(--font-display, var(--font-sans)); font-size:var(--pos-text-lg); font-weight:var(--pos-weight-semibold); color:var(--pos-fg); }
  .dhk8-kds__brand-glyph {
    width:28px; height:28px; display:inline-flex; align-items:center; justify-content:center;
    border-radius:var(--pos-radius-md); color:#fff;
    background:linear-gradient(160deg, var(--hig-green-500), var(--hig-green-700));
    box-shadow:var(--pos-shadow-emboss);
  }
  .dhk8-kds__title { font-family:var(--font-display, var(--font-sans)); }
  .dhk8-kds__station {
    font-family:var(--font-sans); font-size:var(--pos-text-xxs); font-weight:var(--pos-weight-medium);
    letter-spacing:var(--pos-tracking-wide); text-transform:uppercase; color:var(--pos-fg-tertiary);
    padding:2px var(--pos-space-2); border-radius:var(--pos-radius-pill); background:var(--pos-elevated);
  }
  .dhk8-kds__station-select {
    appearance:none; -webkit-appearance:none;
    font-family:var(--font-sans); font-size:var(--pos-text-xs); font-weight:var(--pos-weight-medium);
    color:var(--pos-fg); background:var(--pos-elevated);
    border:1px solid var(--pos-separator-strong); border-radius:var(--pos-radius-pill);
    padding:6px 28px 6px var(--pos-space-3);
    min-height:32px; cursor:pointer; outline:none;
    background-image:linear-gradient(45deg, transparent 50%, var(--pos-fg-tertiary) 50%), linear-gradient(135deg, var(--pos-fg-tertiary) 50%, transparent 50%);
    background-position: calc(100% - 16px) 50%, calc(100% - 11px) 50%;
    background-size: 5px 5px, 5px 5px;
    background-repeat: no-repeat;
  }
  .dhk8-kds__station-select:hover { background-color:var(--pos-tint-soft); }
  .dhk8-kds__station-select:focus-visible { border-color:var(--pos-tint); box-shadow:0 0 0 3px var(--pos-tint-soft); }
  .dhk8-kds__bar-right { display:flex; align-items:center; gap:var(--pos-space-2); }

  .dhk8-kds__live {
    display:inline-flex; align-items:center; gap:6px; font-family:var(--font-sans);
    font-size:var(--pos-text-xxs); font-weight:var(--pos-weight-medium);
    letter-spacing:var(--pos-tracking-wide); text-transform:uppercase; color:var(--pos-fg-tertiary);
    padding:4px var(--pos-space-2); border-radius:var(--pos-radius-pill); background:var(--pos-elevated);
  }
  .dhk8-kds__live--on { color:var(--pos-tint); background:var(--pos-tint-soft); }
  .dhk8-kds__live .dhk8-kds__dot { width:7px; height:7px; border-radius:var(--radius-dot); background:var(--pos-fg-tertiary); }
  .dhk8-kds__live--on .dhk8-kds__dot {
    background:var(--pos-success);
    box-shadow:0 0 0 0 color-mix(in oklch, var(--pos-success) 55%, transparent);
    animation:dhk8-kds-pulse 2s infinite;
  }
  @keyframes dhk8-kds-pulse {
    70%  { box-shadow:0 0 0 6px color-mix(in oklch, var(--pos-success) 0%, transparent); }
    100% { box-shadow:0 0 0 0 color-mix(in oklch, var(--pos-success) 0%, transparent); }
  }

  .dhk8-kds__icon {
    width:44px; height:44px; display:inline-flex; align-items:center; justify-content:center;
    border:1px solid transparent; border-radius:var(--pos-radius-md);
    background:var(--pos-elevated); color:var(--pos-fg-secondary); cursor:pointer;
    transition:background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out);
  }
  .dhk8-kds__icon:hover:not(:disabled) { background:var(--pos-tint-soft); color:var(--pos-fg); }
  .dhk8-kds__icon:active:not(:disabled) { background:var(--pos-tint-hover); }
  .dhk8-kds__icon--active { background:var(--pos-tint); color:#fff; box-shadow:var(--pos-shadow-1); }
  .dhk8-kds__icon--active:hover:not(:disabled) { background:var(--pos-tint); color:#fff; }
  .dhk8-kds__icon:disabled { opacity:.5; cursor:default; }
  .dhk8-kds__icon:focus-visible { outline:2px solid var(--pos-tint); outline-offset:2px; }

  .dhk8-kds__note {
    padding:var(--pos-space-6); text-align:center; font-family:var(--font-sans); font-size:var(--pos-text-sm);
    color:var(--pos-fg-tertiary); display:flex; flex-direction:column; align-items:center; gap:var(--pos-space-3);
    background:var(--pos-surface); border-radius:var(--pos-radius-lg); border:1px solid var(--pos-separator);
  }
  .dhk8-kds__note--err { color:var(--pos-danger); }
  .dhk8-kds__note button {
    min-height:44px; padding:6px 16px; cursor:pointer; font:inherit; font-weight:var(--pos-weight-medium);
    background:var(--pos-tint); color:#fff; border:none; border-radius:var(--pos-radius-md);
    box-shadow:var(--pos-shadow-1);
  }
  .dhk8-kds__note button:hover { background:var(--pos-tint-strong); }
  .dhk8-kds__note--err button { background:var(--pos-danger); }

  .dhk8-kds__board { flex:1; display:grid; grid-template-columns:repeat(3,1fr); gap:var(--pos-space-3); min-height:0; }
  /* tablet portrait (≤640px, e.g. 534×738) → single column for a kitchen tablet */
  @media (max-width:640px) {
    .dhk8-kds__board { grid-template-columns:1fr; gap:var(--pos-space-2); }
    .dhk8-kds__bar { gap:var(--pos-space-2); }
    .dhk8-kds__station { display:none; }
  }

  /* ── Item-aware KDS column + card (estado por item) ───────────────────────
     Espejo del chrome de @dockethub/ui-restaurant <KdsColumn>/<TicketCard> pero
     con controles por ítem. Clases propias (dhk8-kdsi-*) para no depender de los
     estilos inyectados por el componente compartido. */
  .dhk8-kdsi-col { display:flex; flex-direction:column; min-height:0; border:1px solid var(--pos-separator,rgba(255,255,255,.07)); background:var(--pos-surface,#161C18); border-radius:var(--pos-radius-lg,12px); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); overflow:hidden; }
  .dhk8-kdsi-col__head { display:flex; align-items:center; justify-content:space-between; padding:var(--pos-space-3,12px); border-bottom:1px solid var(--pos-separator,rgba(255,255,255,.07)); background:var(--pos-elevated,#1E2520); font-family:var(--font-display,inherit); font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); letter-spacing:.04em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-kdsi-col__count { background:color-mix(in oklch,var(--pos-fg,#E8EFE9) 8%,transparent); color:var(--pos-fg-secondary,#B6BFB8); padding:1px 8px; font-variant-numeric:tabular-nums; border-radius:var(--pos-radius-pill,999px); }
  .dhk8-kdsi-col__list {
    flex:1; min-height:0; overflow-y:auto; -webkit-overflow-scrolling:touch; overscroll-behavior:contain;
    padding:var(--pos-space-2,8px); display:flex; flex-direction:column; gap:var(--pos-space-2,8px);
  }
  /* Scrollbar visible: en cocina (tablet) hay que VER que la columna tiene más abajo. */
  .dhk8-kdsi-col__list { scrollbar-width:thin; scrollbar-color:var(--pos-separator-strong,rgba(255,255,255,.22)) transparent; }
  .dhk8-kdsi-col__list::-webkit-scrollbar { width:10px; }
  .dhk8-kdsi-col__list::-webkit-scrollbar-thumb { background:var(--pos-separator-strong,rgba(255,255,255,.22)); border-radius:999px; border:3px solid transparent; background-clip:content-box; }
  .dhk8-kdsi-col__list::-webkit-scrollbar-track { background:transparent; }
  .dhk8-kdsi-col__empty { padding:var(--pos-space-6,24px); text-align:center; color:var(--pos-fg-tertiary,#7E8C82); font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); }

  .dhk8-kdsi-ticket { flex-shrink:0; display:flex; flex-direction:column; background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-lg,12px); overflow:hidden; box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-kdsi-ticket--warn { background:color-mix(in oklch,var(--pos-warning,#F1B33B) 7%,var(--pos-surface,#161C18)); }
  .dhk8-kdsi-ticket--danger { background:color-mix(in oklch,var(--pos-danger,#E5484D) 9%,var(--pos-surface,#161C18)); border-color:color-mix(in oklch,var(--pos-danger,#E5484D) 30%,transparent); }
  .dhk8-kdsi-ticket--recalled { background:color-mix(in oklch,var(--pos-warning,#F1B33B) 10%,var(--pos-surface,#161C18)); }
  .dhk8-kdsi-ticket__head { display:flex; align-items:baseline; gap:var(--pos-space-2,8px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border-bottom:1px solid var(--pos-separator,rgba(255,255,255,.07)); background:var(--pos-elevated,#1E2520); font-family:var(--font-display,inherit); font-variant-numeric:tabular-nums; }
  .dhk8-kdsi-ticket__num { font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-kdsi-ticket__table { font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-bold,700); letter-spacing:.04em; color:#fff; background:var(--pos-tint,#1F8B3A); padding:2px 10px; border-radius:var(--pos-radius-pill,999px); }
  .dhk8-kdsi-ticket__station { font-size:var(--pos-text-xxs,11px); letter-spacing:.04em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-kdsi-ticket__prio { font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); color:var(--pos-tint,#1F8B3A); background:color-mix(in oklch,var(--pos-tint,#1F8B3A) 14%,transparent); padding:1px 6px; border-radius:var(--pos-radius-pill,999px); }
  .dhk8-kdsi-ticket__channel { font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-semibold,600); letter-spacing:.04em; text-transform:uppercase; padding:2px 8px; border-radius:var(--pos-radius-pill,999px); }
  .dhk8-kdsi-ticket__channel--takeout { color:#fff; background:var(--pos-warning,#F1B33B); }
  .dhk8-kdsi-ticket__channel--delivery { color:#fff; background:var(--pos-info,#3B82F6); }
  .dhk8-kdsi-ticket__recall { font-size:var(--pos-text-xxs,11px); letter-spacing:.04em; color:var(--pos-warning,#F1B33B); }
  .dhk8-kdsi-ticket__timer { margin-left:auto; font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); padding:1px 8px; border-radius:var(--pos-radius-pill,999px); background:color-mix(in oklch,currentColor 14%,transparent); }
  .dhk8-kdsi-ticket__timer--fresh { color:var(--pos-success,#8BC34A); }
  .dhk8-kdsi-ticket__timer--warn { color:var(--pos-warning,#F1B33B); }
  .dhk8-kdsi-ticket__timer--danger { color:var(--pos-danger,#E5484D); }
  .dhk8-kdsi-ticket__items { list-style:none; margin:0; padding:var(--pos-space-3,12px); display:flex; flex-direction:column; gap:var(--pos-space-3,12px); }
  .dhk8-kdsi-ticket__item { flex:0 0 auto; display:flex; flex-direction:column; gap:var(--pos-space-2,8px); padding-bottom:var(--pos-space-3,12px); border-bottom:1px solid var(--pos-separator,rgba(255,255,255,.06)); }
  .dhk8-kdsi-ticket__item:last-child { border-bottom:none; padding-bottom:0; }
  .dhk8-kdsi-ticket__item--ready .dhk8-kdsi-ticket__item-name { opacity:.5; text-decoration:line-through; }
  .dhk8-kdsi-ticket__item-main { display:flex; gap:var(--pos-space-3,12px); align-items:flex-start; }
  /* La cantidad se lee a distancia en cocina: chip grande y tabular. */
  .dhk8-kdsi-ticket__qty {
    flex-shrink:0; display:inline-flex; align-items:center; justify-content:center;
    min-width:38px; min-height:30px; padding:0 8px;
    font-family:var(--font-display,inherit); font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-bold,700);
    font-variant-numeric:tabular-nums; color:var(--pos-fg,#E8EFE9);
    background:var(--pos-elevated,#1E2520); border-radius:var(--pos-radius-md,8px);
  }
  .dhk8-kdsi-ticket__item-body { display:flex; flex-direction:column; gap:3px; flex:1; min-width:0; }
  /* Nombre del platillo: es LO que el cocinero lee. Grande y con buen interlineado. */
  .dhk8-kdsi-ticket__item-name { font-family:var(--font-display,inherit); font-size:var(--pos-text-lg,17px); font-weight:var(--pos-weight-semibold,600); line-height:1.3; color:var(--pos-fg,#E8EFE9); overflow-wrap:anywhere; }
  .dhk8-kdsi-ticket__mods { margin:0; padding:0; list-style:none; font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); line-height:1.4; color:var(--pos-fg-secondary,#B6BFB8); }
  .dhk8-kdsi-ticket__notes { font-family:var(--font-display,inherit); font-size:var(--pos-text-sm,13px); line-height:1.4; color:var(--pos-warning,#F1B33B); font-style:italic; }
  .dhk8-kdsi-ticket__item-badge { align-self:flex-start; flex-shrink:0; font-size:9px; font-weight:var(--pos-weight-semibold,600); letter-spacing:.06em; text-transform:uppercase; padding:2px 6px; border-radius:var(--pos-radius-pill,999px); }
  .dhk8-kdsi-ticket__item-badge--ready { color:#fff; background:var(--pos-tint,#1F8B3A); }
  .dhk8-kdsi-ticket__item-badge--prep { color:var(--pos-warning,#F1B33B); background:color-mix(in oklch,var(--pos-warning,#F1B33B) 16%,transparent); }
  /* Targets táctiles reales (≥44px) — se operan con dedos grasosos en cocina. */
  .dhk8-kdsi-ticket__item-actions { display:flex; gap:var(--pos-space-2,8px); padding-left:50px; }
  .dhk8-kdsi-item-btn { flex:1 1 0; justify-content:center; display:inline-flex; align-items:center; gap:6px; min-height:var(--pos-touch-target,44px); padding:0 12px; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-semibold,600); letter-spacing:.04em; text-transform:uppercase; cursor:pointer; border-radius:var(--pos-radius-pill,999px); transition:background var(--pos-duration-fast,120ms) ease, color var(--pos-duration-fast,120ms) ease; }
  .dhk8-kdsi-item-btn:hover:not(:disabled) { color:var(--pos-fg,#E8EFE9); background:var(--pos-elevated,#1E2520); }
  .dhk8-kdsi-item-btn:disabled { opacity:.45; cursor:not-allowed; }
  .dhk8-kdsi-item-btn--active { background:color-mix(in oklch,var(--pos-warning,#F1B33B) 18%,transparent); color:var(--pos-warning,#F1B33B); border-color:color-mix(in oklch,var(--pos-warning,#F1B33B) 40%,transparent); }
  .dhk8-kdsi-item-btn--primary { background:var(--pos-tint,#1F8B3A); color:#fff; border-color:var(--pos-tint,#1F8B3A); }
  .dhk8-kdsi-item-btn--primary:hover:not(:disabled) { background:var(--pos-tint-strong,#0E5A2A); color:#fff; }
  .dhk8-kdsi-item-btn--primary.dhk8-kdsi-item-btn--active { background:var(--pos-tint,#1F8B3A); color:#fff; box-shadow:0 0 0 2px color-mix(in oklch,var(--pos-tint,#1F8B3A) 35%,transparent); }
  .dhk8-kdsi-ticket__foot { display:flex; gap:var(--pos-space-1,4px); flex-wrap:wrap; padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border-top:1px solid var(--pos-separator,rgba(255,255,255,.07)); }
  .dhk8-kdsi-ticket__btn { display:inline-flex; align-items:center; gap:4px; min-height:var(--pos-touch-target,44px); padding:0 12px; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font-family:var(--font-display,inherit); font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); letter-spacing:.04em; text-transform:uppercase; cursor:pointer; border-radius:var(--pos-radius-pill,999px); transition:background var(--pos-duration-fast,120ms) ease, transform var(--pos-duration-fast,120ms) ease; }
  .dhk8-kdsi-ticket__btn:hover:not(:disabled) { color:var(--pos-fg,#E8EFE9); background:var(--pos-elevated,#1E2520); }
  .dhk8-kdsi-ticket__btn:active:not(:disabled) { transform:scale(0.97); }
  .dhk8-kdsi-ticket__btn:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-kdsi-ticket__btn--primary, .dhk8-kdsi-ticket__btn--bump { background:var(--pos-tint,#1F8B3A); color:#fff; border-color:var(--pos-tint,#1F8B3A); }
  .dhk8-kdsi-ticket__btn--primary:hover:not(:disabled), .dhk8-kdsi-ticket__btn--bump:hover:not(:disabled) { background:var(--pos-tint-strong,#0E5A2A); color:#fff; }
  .dhk8-kdsi-ticket__btn--ghost { color:var(--pos-fg-tertiary,#7E8C82); border-color:transparent; }
  .dhk8-kdsi-ticket__btn--ghost:hover:not(:disabled) { background:var(--pos-tint-soft,color-mix(in oklch,#1F8B3A 12%,transparent)); color:var(--pos-fg,#E8EFE9); }
`
